import { useState, useEffect, useCallback, useRef } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx } from "../../apiClient";
import { Btn, Inp, Modal } from "../primitives";

// Entity display code (2026-07-02 display-code lane): each entity carries a
// 4-letter code that is STAMPED onto new records' display numbers
// (REQ-<CODE>-0001, per-entity sequences). The code auto-suggests from the
// name (editable), validates against the never-reuse registry (409 on any
// code ever used), and CHANGING it affects future records only — stamped
// history keeps rendering with the code it was created under.
export const CompanyEntitiesManager = () => {
  const [entities, setEntities] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editEntity, setEditEntity] = useState<any>(null);
  const [form, setForm] = useState({ name: "", code: "", is_default: false, is_active: true, sort_order: 0 });
  const [saveError, setSaveError] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const suggestTimer = useRef<any>(null);

  const load = useCallback(async () => {
    const r = await apiCall("/company-entities");
    if (r?.entities) setEntities(r.entities);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Auto-suggest a code while the admin types the name — only until the code
  // field has been edited by hand, and only for entities without a code yet.
  const onNameChange = (v: string) => {
    setForm(p => ({ ...p, name: v }));
    if (codeTouched || (editEntity && editEntity.code)) return;
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestTimer.current = setTimeout(async () => {
      if (!v.trim()) return;
      const r = await apiCall(`/company-entities/suggest-code?name=${encodeURIComponent(v.trim())}`);
      if (r?.code) setForm(p => (codeTouched ? p : { ...p, code: r.code }));
    }, 400);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaveError("");
    const body: any = { ...form, code: form.code.trim() ? form.code.trim().toUpperCase() : undefined };
    if (body.code === undefined) delete body.code;
    const r = editEntity
      ? await apiCallEx("/company-entities/" + editEntity.id, { method: "PUT", body: JSON.stringify(body) })
      : await apiCallEx("/company-entities", { method: "POST", body: JSON.stringify(body) });
    if (!r.ok) {
      setSaveError(r.data?.error || "Save failed");
      return;
    }
    setShowAdd(false); setEditEntity(null); setCodeTouched(false);
    setForm({ name: "", code: "", is_default: false, is_active: true, sort_order: 0 });
    void load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Deactivate this entity?")) return;
    await apiCall("/company-entities/" + id, { method: "DELETE" });
    void load();
  };

  const handleSetDefault = async (id: string) => {
    await apiCall("/company-entities/" + id, { method: "PUT", body: JSON.stringify({ is_default: true }) });
    void load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Company Entities</div>
        <Btn v="primary" sz="sm" onClick={() => { setForm({ name: "", code: "", is_default: false, is_active: true, sort_order: 0 }); setEditEntity(null); setCodeTouched(false); setSaveError(""); setShowAdd(true); }}>Add Entity</Btn>
      </div>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead><tr style={{ borderBottom: "2px solid " + T.brd }}>
          <th style={{ textAlign: "left", padding: 8 }}>Name</th>
          <th style={{ textAlign: "center", padding: 8 }}>Code</th>
          <th style={{ textAlign: "center", padding: 8 }}>Default</th>
          <th style={{ textAlign: "center", padding: 8 }}>Active</th>
          <th style={{ textAlign: "center", padding: 8 }}>Sort</th>
          <th style={{ textAlign: "right", padding: 8 }}>Actions</th>
        </tr></thead>
        <tbody>
          {entities.map(e => (
            <tr key={e.id} style={{ borderBottom: "1px solid " + T.brd }}>
              <td style={{ padding: 8 }}>{e.name}</td>
              <td style={{ padding: 8, textAlign: "center", fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>{e.code || "—"}</td>
              <td style={{ padding: 8, textAlign: "center" }}>{e.is_default ? "✓" : ""}</td>
              <td style={{ padding: 8, textAlign: "center" }}>{e.is_active ? "✓" : "✗"}</td>
              <td style={{ padding: 8, textAlign: "center" }}>{e.sort_order}</td>
              <td style={{ padding: 8, textAlign: "right" }}>
                <span onClick={() => { setEditEntity(e); setForm({ name: e.name, code: e.code || "", is_default: e.is_default, is_active: e.is_active, sort_order: e.sort_order }); setCodeTouched(false); setSaveError(""); setShowAdd(true); }} style={{ color: T.acc, cursor: "pointer", marginRight: 12, fontSize: 11 }}>Edit</span>
                {!e.is_default && <span onClick={() => { void handleSetDefault(e.id); }} style={{ color: T.blu, cursor: "pointer", marginRight: 12, fontSize: 11 }}>Set Default</span>}
                {!e.is_default && <span onClick={() => { void handleDelete(e.id); }} style={{ color: T.red, cursor: "pointer", fontSize: 11 }}>Deactivate</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showAdd && (
        <Modal open onClose={() => { setShowAdd(false); setEditEntity(null); }} title={editEntity ? "Edit Entity" : "Add Entity"}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Inp label="Name" value={form.name} onChange={onNameChange} placeholder="e.g., Erlichman Law, PLLC" />
            <div>
              <Inp label="Display Code (4 letters)" value={form.code} onChange={(v: string) => { setCodeTouched(true); setForm(p => ({ ...p, code: v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4) })); }} placeholder="auto-suggested from name" />
              <div style={{ fontSize: 10, color: T.txD, marginTop: 3 }}>Stamped on new records (REQ-{form.code || "CODE"}-0001, per-entity numbering). Codes are never reused; changing it affects future records only — existing records keep the code they were created under.</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} />
              <span style={{ fontSize: 12 }}>Default entity</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
              <span style={{ fontSize: 12 }}>Active</span>
            </div>
            {saveError && <div style={{ fontSize: 12, color: T.red }}>{saveError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <Btn v="secondary" onClick={() => { setShowAdd(false); setEditEntity(null); }}>Cancel</Btn>
              <Btn v="primary" onClick={() => { void handleSave(); }}>{editEntity ? "Update" : "Create"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
